export type SkillLevel = 'beginner' | 'intermediate' | 'advanced' | 'professional';

// V2 player profile enums
export type Gender = 'male' | 'female' | 'other' | 'prefer_not_to_say';
export type PlayerType = 'beginner' | 'developing' | 'recreational' | 'competitive' | 'junior';

export const COACH_SPECIALTIES = [
  'Serve Technique',
  'Footwork',
  'Mental Game',
  'Match Strategy',
  'Forehand',
  'Backhand',
  'Volleys',
  'Singles',
  'Doubles'
] as const;

export type CoachSpecialty = typeof COACH_SPECIALTIES[number];

export interface NotificationPreferences {
  likes: boolean;
  comments: boolean;
  follows: boolean;
  messages: boolean;
  lessonRequests: boolean;
  lessonUpdates: boolean;
}

// ============================================
// New Profile Structure (Embedded Objects)
// ============================================

/**
 * Base profile fields shared by both Coach and Player profiles
 */
export interface BaseProfile {
  username?: string;
  displayName: string;
  avatarUrl?: string | null;
  bio?: string | null;
  followersCount?: number;
  followingCount?: number;
}

/**
 * Player-specific profile fields
 */
export interface PlayerProfile extends BaseProfile {
  location?: string | null;
  skillLevel?: SkillLevel | null;
  tennisBackground?: string | null;
  utr?: number;

  // V2 fields
  /** @deprecated No longer collected or written. Retained only to read existing docs. */
  gender?: Gender | null;
  age?: number | null;
  /** @deprecated No longer collected or written. Retained only to read existing docs. */
  playerType?: PlayerType | null;
  ntrp?: number | null;                          // NTRP rating e.g. 3.5, 4.0
  shotConsultCredits?: number;                   // Starts at 1; decremented to 0 when used

  // Referral program — set once, never overwritten
  referredBy?: {
    coachId: string;
    referralCode: string;
    source: 'link' | 'manual';
    setAt: string;            // ISO timestamp
    manualSource?: string;    // free-text label when source === 'manual'
  };
}

/**
 * Coach verification status
 * - 'none': Never applied to be a coach
 * - 'pending': Application submitted, awaiting admin review
 * - 'verified': Approved by admin, full coach access
 * - 'rejected': Application rejected by admin
 */
export type CoachVerificationStatus = 'none' | 'pending' | 'verified' | 'rejected';

/**
 * Coach-specific profile fields (superset of base profile)
 */
export interface CoachProfile extends BaseProfile {
  // Tennis credentials
  experience?: number;
  utr?: number;
  worksWithLevels?: SkillLevel[];

  // Coach presentation
  shortDescriptor?: string;
  achievements?: string[];
  specialties?: CoachSpecialty[];
  sampleVideoUrl?: string;

  // Reviews
  averageReviewRating?: number;
  totalReviews?: number;

  // Verification
  verificationStatus: CoachVerificationStatus;

  // V2 fields
  gender?: Gender | null;
  highestLevel?: string[] | null;                // e.g. ["NCAA D1", "ATP"] — multi-select on onboarding
  // Career-high tour rankings — parallel numeric fields populated from the
  // onboarding pills. ATP for male coaches, WTA for female coaches; ITF
  // gender-neutral. Legacy encoded entries on `highestLevel` (e.g. "ATP
  // Career High: 197") are still written for backward compatibility.
  atpCareerHigh?: number | null;
  wtaCareerHigh?: number | null;
  itfCareerHigh?: number | null;
  certifications?: string[] | null;              // e.g. ["PTR Pro", "USPTA Elite"]
  yearsCoaching?: number | null;
  /** @deprecated Never written. Nothing in either repo or in Cloud Functions
   *  has ever populated this outside the Firestore emulator, so it reads
   *  undefined on every production coach. The lesson count shown in the UI is
   *  derived at read time from `orders`; see services/coachLessonCounts.ts. */
  completedLessonsCount?: number;
  lessonPrice?: number | null;                   // Price in cents for a single lesson
  isAcceptingClients?: boolean;                  // Capacity toggle — false hides from discovery
  freeConsultEnabled?: boolean;                  // Whether the coach offers the free first Shot Consult (default true)
  avgResponseHours?: number | null;              // Rolling avg hours to accept/decline
  verificationRequestedAt?: string;
  verifiedAt?: string;
  rejectionReason?: string;

  // Public profile URL slug — e.g. "sarah-chen-serve-specialist-austin"
  slug?: string;
  /**
   * Chosen hero background on the public profile: one of the four court names
   * or an https URL to the coach's own upload. Unset falls through to the uid
   * hash the CMS already uses, so an existing coach keeps the image they have
   * until they pick. The CMS branches on whether it starts with http.
   */
  heroSurface?: string;
  /**
   * The coach's last uploaded hero, remembered even while a court surface is
   * selected — so switching back to their own image costs no re-upload. The
   * file stays in Storage for the same reason.
   */
  heroCustomUrl?: string;

  // Referral program
  referralCode?: string;            // deterministic 8-char code used in shareable links
  hasReferralSlot?: boolean;        // admin-granted: enables the coach-on-coach referral form
  activeCoachReferralId?: string;   // ID of the active CoachReferralSubmission (if any)

  // Admin flags
  foundingCoach?: boolean;       // One of the first coaches; surfaces a "Founding Coach" badge in UI

  // Stripe Connect (earnings / payouts)
  stripeAccountId?: string;           // Stripe Express account ID
  stripeAccountStatus?: 'not_connected' | 'pending' | 'connected';
  stripePayoutsEnabled?: boolean;
  stripeChargesEnabled?: boolean;
}

/**
 * User account type - determines which mode the user is currently in
 */
export type ProfileMode = 'coach' | 'player';

/**
 * Platform role for access control.
 * - 'player' / 'coach': end users
 * - 'support': customer-support staff; can reply in the Double Bagel support channel
 * - 'sales': limited admin panel access — Invitations + Lesson Organizer (non-production)
 * - 'accounts': limited admin panel access — Billing + Reconcile
 * - 'admin': internal staff with full admin panel access
 * - 'root': superuser; can manage admin/support users; all access
 */
export type UserRole = 'player' | 'coach' | 'support' | 'sales' | 'accounts' | 'admin' | 'root';

/**
 * Roles that can be granted via a staff invitation
 */
export type StaffRole = 'admin' | 'support' | 'sales' | 'accounts';

/**
 * Staff invitation document (staffInvitations collection)
 */
export interface StaffInvitation {
  id: string;
  email: string;
  role: StaffRole;
  token: string;
  status: 'pending' | 'accepted';
  invitedBy: string;       // UID of root user who created the invite
  invitedByName: string;
  createdAt: string;       // ISO timestamp
  acceptedAt?: string;
}

/**
 * A single field change within an admin action.
 * `from` is the value before the action; `to` is the value after.
 * Rollback = write `from` back. Re-apply = write `to` back.
 */
export interface FieldDelta {
  from: unknown;
  to: unknown;
}

/**
 * Admin audit event (adminActions collection).
 * Delta-based: each changed field is recorded individually so
 * incremental rollbacks can restore state at any prior point.
 */
export interface AdminAction {
  id: string;
  actorId: string;
  actorName: string;
  actorRole: UserRole;
  action: string;             // snake_case key e.g. 'deactivate_user'
  actionLabel: string;        // human-readable e.g. 'Deactivate User'
  targetType: string;         // 'user' | 'lesson' | 'staff_invitation'
  targetCollection: string;   // Firestore collection e.g. 'users', 'conversations'
  targetId: string;
  targetLabel?: string | null;// display name e.g. "John Smith (coach)"
  changes: Record<string, FieldDelta>; // fieldPath → { from, to }
  rollbackable: boolean;
  rolledBackAt: string | null;
  rolledBackBy: string | null;
  rolledBackByName: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
}

/**
 * Main user account containing both embedded profiles
 */
export interface UserAccount {
  uid: string;
  email: string;
  currentMode: ProfileMode;

  // Embedded profiles
  playerProfile?: PlayerProfile;
  coachProfile?: CoachProfile;

  // Role — primary access-control field
  role?: UserRole;

  isActive?: boolean;
  deactivatedByUser?: boolean;
  deactivatedAt?: string;

  // Email verification
  emailVerified?: boolean;

  // Notifications
  fcmToken?: string;
  notificationPreferences?: NotificationPreferences;

  // Stripe (player billing)
  stripeCustomerId?: string;          // Stripe Customer ID — set on first payment

  // Legal acceptance tracking
  acceptedPlayerTermsVersion?: number;
  acceptedCoachTermsVersion?: number;
  acceptedPrivacyVersion?: number;
  playerTermsAcceptedAt?: string;  // ISO timestamp
  coachTermsAcceptedAt?: string;   // ISO timestamp
  privacyAcceptedAt?: string;      // ISO timestamp

  // Metadata
  createdAt: string;
  updatedAt: string;
}

// ============================================
// Legacy Types (Backward Compatibility)
// ============================================

/**
 * @deprecated Use ProfileMode instead
 *
 * 'parent' was added for COPPA — when the under-13 intercept fires, the account
 * holder is the parent and the player is the youth profile attached to the
 * parent's user doc. Coach-side surfaces render "Player: X (Parent: Y)".
 */
export type AccountType = 'coach' | 'player' | 'parent';

/**
 * Legacy UserProfile interface for backward compatibility during migration.
 * New code should use UserAccount with embedded playerProfile/coachProfile.
 *
 * This interface merges all fields at the root level for compatibility
 * with existing code that reads directly from UserProfile.
 */
export interface UserProfile {
  uid: string;
  email: string;

  /** @deprecated Use currentMode instead */
  accountType: AccountType;
  /** Current active profile mode */
  currentMode?: ProfileMode;

  // Profile Info (legacy - now in playerProfile/coachProfile)
  username?: string;
  displayName?: string;
  bio?: string | null;
  avatarUrl?: string | null;
  skillLevel?: SkillLevel | null;
  location?: string | null;
  tennisBackground?: string | null;
  utr?: number;

  // Coach-specific (legacy - now in coachProfile)
  /** @deprecated Use coachProfile.verificationStatus === 'verified' instead */
  isVerified?: boolean;
  shortDescriptor?: string;
  achievements?: string[];
  specialties?: CoachSpecialty[];
  sampleVideoUrl?: string;
  experience?: number;
  worksWithLevels?: SkillLevel[];
  averageReviewRating?: number;
  totalReviews?: number;

  // New embedded profiles
  playerProfile?: PlayerProfile;
  coachProfile?: CoachProfile;

  // Role — primary access-control field
  role?: UserRole;

  isActive?: boolean;
  deactivatedByUser?: boolean;
  deactivatedAt?: string;

  // ── COPPA / parent-account fields (set only on accountType === 'parent') ──
  // The "player" on a parent account is the youth profile. These fields capture
  // the child's identity + the parent's identity + the verifiable parental
  // consent (VPC) artifact. The VPC fields below are written exclusively by
  // the verifyParentWithCard Cloud Function; Firestore rules prevent client
  // self-writes. See ParentStep3 + functions/src/stripeService.ts.
  playerFirstName?: string;          // child's first name (only field collected for the youth profile)
  playerDOB?: string;                // child's DOB as ISO string (Firestore Timestamp on the wire)
  parentFirstName?: string;
  parentLastName?: string;
  parentalConsentSignature?: string; // parent's typed legal name from Step 3
  parentalConsentSignedAt?: string;  // ISO timestamp
  parentalConsentMethod?: 'credit_card_verification';
  /** True once the Stripe auth+void has succeeded. CF-only write. */
  parentalConsentVerified?: boolean;
  parentalConsentVerifiedAt?: string;
  /** Stripe PaymentIntent ID used for the $0.50 auth hold (never captured). CF-only. */
  stripeVerificationAuthId?: string;
  /** True once the auth has been cancelled (voided). CF-only. */
  stripeVerificationAuthVoided?: boolean;

  // 13-17 player-account flag — the player is between 13 and 17 and at signup
  // affirmed that a parent has reviewed/approved their use of the Service.
  parentalAffirmation?: boolean;
  parentalAffirmationAt?: string;

  // Email verification
  emailVerified?: boolean;
  // Real email for Apple users who chose "Hide My Email"; relay address stays
  // in `email` (matches Firebase Auth), this is used for all outbound mail.
  contactEmail?: string;

  // Notifications
  fcmToken?: string;
  notificationPreferences?: NotificationPreferences;

  // Stripe (player billing)
  stripeCustomerId?: string;          // Stripe Customer ID — set on first payment

  // Social
  followersCount?: number;
  followingCount?: number;

  // Legal
  termsAcceptedAt?: string;  // ISO 8601 timestamp when user accepted Terms & Privacy Policy

  // Legal acceptance tracking (versioned)
  acceptedPlayerTermsVersion?: number;
  acceptedCoachTermsVersion?: number;
  acceptedPrivacyVersion?: number;
  playerTermsAcceptedAt?: string;  // ISO timestamp
  coachTermsAcceptedAt?: string;   // ISO timestamp
  privacyAcceptedAt?: string;      // ISO timestamp

  // Referral program — mirrored from playerProfile.referredBy for easy Firestore querying
  referredBy?: {
    coachId: string;
    referralCode: string;
    source: 'link' | 'manual';
    setAt: string;
    manualSource?: string;
  };

  // Credits
  /** Platform credit balance in cents. 0 when absent. */
  creditBalanceCents?: number;

  // Metadata
  createdAt: string;
  updatedAt: string;
}

// ============================================
// Helper Types
// ============================================

/**
 * Data required to create a new player profile
 */
export interface CreatePlayerProfileData {
  displayName: string;
  username?: string;
  bio?: string | null;
  skillLevel?: SkillLevel | null;
  location?: string | null;
  tennisBackground?: string | null;
  utr?: number;
}

/**
 * Data required to create a new coach profile
 */
export interface CreateCoachProfileData {
  displayName: string;
  username?: string;
  bio?: string | null;
  location?: string | null;
  experience?: number;
  utr?: number;
  worksWithLevels?: SkillLevel[];
  shortDescriptor?: string;
  achievements?: string[];
  specialties?: CoachSpecialty[];
}

/**
 * Data for updating a player profile
 */
// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface UpdatePlayerProfileData extends Partial<Omit<PlayerProfile, 'followersCount' | 'followingCount'>> {}

/**
 * Data for updating a coach profile (excludes admin-only fields)
 */
// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface UpdateCoachProfileData extends Partial<Omit<CoachProfile, 'verificationStatus' | 'verificationRequestedAt' | 'verifiedAt' | 'rejectionReason' | 'foundingCoach' | 'followersCount' | 'followingCount' | 'averageReviewRating' | 'totalReviews'>> {}

/**
 * Helper to check if a user has a verified coach profile
 */
export function isCoachVerified(user: UserProfile | UserAccount): boolean {
  return user.coachProfile?.verificationStatus === 'verified';
}

/**
 * Helper to check if a user has a pending coach verification
 */
export function isCoachVerificationPending(user: UserProfile | UserAccount): boolean {
  return user.coachProfile?.verificationStatus === 'pending';
}

/**
 * Helper to get the active profile based on current mode
 */
export function getActiveProfile(user: UserProfile | UserAccount): PlayerProfile | CoachProfile | undefined {
  const mode = user.currentMode || (user as UserProfile).accountType || 'player';
  return mode === 'coach' ? user.coachProfile : user.playerProfile;
}

/**
 * Helper to get display name from active profile
 */
export function getActiveDisplayName(user: UserProfile | UserAccount): string {
  const activeProfile = getActiveProfile(user);
  return activeProfile?.displayName || (user as UserProfile).displayName || 'User';
}

/**
 * Helper to get avatar URL from active profile
 */
export function getActiveAvatarUrl(user: UserProfile | UserAccount): string | null | undefined {
  const activeProfile = getActiveProfile(user);
  return activeProfile?.avatarUrl || (user as UserProfile).avatarUrl;
}
